/* eslint-disable @typescript-eslint/no-non-null-assertion */
import { useMemo, useState } from "react";

import clsx from "clsx";
import { AnimatePresence, motion } from "framer-motion";
import { groupBy } from "lodash";

import { useDesignElementManager } from "@hooks/use-design-element-manager";
import {
  type FetchMediaPlanData_me_mmm_mediaPlanData_mediaPlanList,
  type FetchMediaPlanData_me_mmm_mediaPlanData_mediaPlanList_spend_model_data_columns,
} from "@north-beam/nb-client-side/nb-api-graphql-generated";
import { VERIFY_CHANNELS } from "@north-beam/nb-common";
import { IconButton, PrimaryButton, TertiaryButton } from "@shared/buttons";
import { Modal } from "@shared/modal";
import { TextInput } from "@shared/text-input";

import { useUpdateMediaPlanDraftStep } from "../../hooks/use-update-media-plan-draft-step";
import { DEPRECATED_WIZARD_STEPS as WIZARD_STEPS } from "../consts";

import { MediaPlanVerifyButton } from "./media-plan-verify-button";

interface Props {
  currentIndex: number;
  spendModelDataColumns?: FetchMediaPlanData_me_mmm_mediaPlanData_mediaPlanList_spend_model_data_columns[];
  selectedMediaPlan: FetchMediaPlanData_me_mmm_mediaPlanData_mediaPlanList;
}

export const ChannelsStep = ({
  currentIndex,
  spendModelDataColumns = [],
  selectedMediaPlan,
}: Props) => {
  const { designElementManager } = useDesignElementManager();
  const isDraft = selectedMediaPlan.state === "draft";

  const [showPendingDataModal, setShowPendingDataModal] = useState(false);
  const [pendingValue, setPendingValue] = useState("");
  const closeModal = () => {
    setPendingValue("");
    setShowPendingDataModal(false);
  };

  const {
    verify,
    unverify,
    addPendingData,
    removePendingData,
    isUpdateMediaPlanDraftStepLoading,
  } = useUpdateMediaPlanDraftStep({
    draftStep: VERIFY_CHANNELS,
    selectedMediaPlan,
  });

  const isVerified = selectedMediaPlan.step_tracker[VERIFY_CHANNELS].verified;
  const pendingData = useMemo(
    () => selectedMediaPlan.step_tracker[VERIFY_CHANNELS].pending_data,
    [selectedMediaPlan],
  );

  const _verify = async () => verify(spendModelDataColumns);
  const _addPendingData = async (newPendingData: string) => {
    await addPendingData(newPendingData);
    closeModal();
  };

  return (
    <div>
      <div className="mb-4">
        <div className="text-lg font-semibold">
          {WIZARD_STEPS[currentIndex].title}
        </div>
        {WIZARD_STEPS[currentIndex].description && isDraft && (
          <div className="text-sm text-interstellar-1">
            {WIZARD_STEPS[currentIndex].description}
          </div>
        )}
      </div>
      <div className="text-xs font-bold border-b border-b-[#E5E5E5] pb-1">
        MMM CHANNELS
      </div>
      <div
        className={clsx("overflow-y-auto", {
          "max-h-[360px]": pendingData.length === 0 && isDraft,
          "max-h-[420px]": pendingData.length === 0 && !isDraft,
          "max-h-[180px]": pendingData.length > 0,
        })}
      >
        {spendModelDataColumns.length > 0 ? (
          <ChannelBreakdownList spendModelDataColumns={spendModelDataColumns} />
        ) : (
          <div className="text-sm text-interstellar-1 py-2">
            No channels added
          </div>
        )}
      </div>
      {pendingData.length > 0 && (
        <>
          <div className="text-xs font-bold border-b border-b-[#E5E5E5] pt-4 pb-1">
            PENDING CHANNELS
          </div>
          <div className="overflow-y-auto max-h-[160px]">
            {pendingData.map((pendingDatum, index) => (
              <div
                key={pendingDatum}
                className="py-2 text-xs flex items-center justify-between gap-x-1.5 border-b border-b-[#E5E5E5]"
              >
                <div className="flex items-center gap-2">
                  {designElementManager
                    .getDesignElement(
                      pendingDatum.split(" - ")[0].split(" / ")[0] ?? "",
                    )
                    .getComponent({
                      size: "24px",
                    })}
                  {pendingDatum}
                </div>
                <IconButton
                  variant="tertiary"
                  onClick={() => removePendingData(index)}
                  loading={isUpdateMediaPlanDraftStepLoading}
                >
                  <i className="fa-regular fa-trash-can text-danger" />
                </IconButton>
              </div>
            ))}
          </div>
        </>
      )}
      <MediaPlanVerifyButton
        className="mt-4"
        verify={_verify}
        unverify={unverify}
        isVerified={isVerified}
        isLoading={isUpdateMediaPlanDraftStepLoading}
      >
        <IconButton
          variant="tertiary"
          className="px-2"
          leftIcon={<i className="fa-regular fa-plus" />}
          onClick={() => setShowPendingDataModal(true)}
        >
          ADD NEW CHANNEL
        </IconButton>
      </MediaPlanVerifyButton>
      <Modal
        isOpen={showPendingDataModal}
        onClose={closeModal}
        titleContent={<div className="font-bold text-xl">Add New Channel</div>}
      >
        <TextInput
          label="Channel Name"
          isRequired
          placeholder="Channel Name"
          value={pendingValue}
          onChange={(e) => setPendingValue(e.target.value)}
        />
        <div className="mt-4 flex items-center justify-end">
          <TertiaryButton
            loading={isUpdateMediaPlanDraftStepLoading}
            isDisabled={isUpdateMediaPlanDraftStepLoading}
            onClick={closeModal}
          >
            CANCEL
          </TertiaryButton>
          <PrimaryButton
            loading={isUpdateMediaPlanDraftStepLoading}
            isDisabled={isUpdateMediaPlanDraftStepLoading}
            onClick={async () => {
              await _addPendingData(pendingValue);
              setShowPendingDataModal(false);
            }}
          >
            ADD CHANNEL
          </PrimaryButton>
        </div>
      </Modal>
    </div>
  );
};

export const ChannelBreakdownList = ({
  spendModelDataColumns,
}: {
  spendModelDataColumns: FetchMediaPlanData_me_mmm_mediaPlanData_mediaPlanList_spend_model_data_columns[];
}) => {
  const { designElementManager } = useDesignElementManager();

  const hasMultipleBreakdowns = useMemo(
    () =>
      spendModelDataColumns[0]?.mta_breakdowns?.length &&
      spendModelDataColumns[0].mta_breakdowns.length > 1,
    [spendModelDataColumns],
  );

  const [expandedBreakdowns, setExpandedBreakdowns] = useState<string[]>([]);
  if (hasMultipleBreakdowns) {
    const groupedColumns = groupBy(
      spendModelDataColumns,
      (column) => column.mta_breakdowns![0].value,
    );
    return Object.entries(groupedColumns).map(([breakdown, columns]) => (
      <div key={breakdown} className="py-2 text-xs border-b border-b-[#E5E5E5]">
        <div
          className="flex items-center gap-2 cursor-pointer"
          onClick={() => {
            if (expandedBreakdowns.includes(breakdown)) {
              setExpandedBreakdowns(
                expandedBreakdowns.filter((b) => b !== breakdown),
              );
            } else {
              setExpandedBreakdowns([...expandedBreakdowns, breakdown]);
            }
          }}
        >
          {expandedBreakdowns.includes(breakdown) ? (
            <i className="fa-regular fa-chevron-down fill-interstellar" />
          ) : (
            <i className="fa-regular fa-chevron-right fill-interstellar" />
          )}
          {designElementManager.getDesignElement(breakdown).getComponent({
            size: "24px",
          })}
          {breakdown}
        </div>
        <AnimatePresence initial={false}>
          {expandedBreakdowns.includes(breakdown) && (
            <motion.div
              initial={{ height: 0 }}
              animate={{ height: "auto" }}
              exit={{ height: 0 }}
              transition={{ ease: "easeOut" }}
              className="overflow-hidden"
            >
              {columns.map((column) => (
                <div
                  key={column.id}
                  className="pl-5 py-2 text-xs border-b border-b-[#E5E5E5] last:border-b-0 last:pb-0 bg-v2-primary"
                >
                  {column.mta_breakdowns![1].value}
                </div>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    ));
  }

  return spendModelDataColumns.map((column) => (
    <div
      key={column.id}
      className="py-2 text-xs flex items-center gap-x-1.5 border-b border-b-[#E5E5E5]"
    >
      {designElementManager
        .getDesignElement(
          column.friendly_name.split(" - ")[0].split(" / ")[0] ?? "",
        )
        .getComponent({
          size: "24px",
        })}
      {column.friendly_name}
    </div>
  ));
};
